import { api } from '@common';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useState, useEffect } from 'react';

const GUEST_CART_KEY = 'guest_cart';

export interface CartItem {
  id: string;
  productId: string;
  variantId?: string;
  productName: string;
  productPrice: number;
  productImage: string;
  size?: string | null;
  quantity: number;
  subtotal: number;
}

export interface Cart {
  id?: string;
  items: CartItem[];
  totalItems: number;
  totalAmount: number;
}

export const useCart = (isAuthenticated?: boolean) => {
  const queryClient = useQueryClient();
  const [guestCart, setGuestCart] = useState<Cart>(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem(GUEST_CART_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return { items: [], totalItems: 0, totalAmount: 0 };
      }
    }
    return { items: [], totalItems: 0, totalAmount: 0 };
  });

  // Fetch authenticated user's cart
  const { data: serverCart, isLoading } = useQuery<Cart>({
    queryKey: ['cart'],
    queryFn: async () => {
      const response = await api.get('/cart');
      return response.data;
    },
    enabled: !!isAuthenticated,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Sync guest cart to localStorage
  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.setItem(GUEST_CART_KEY, JSON.stringify(guestCart));
    }
  }, [guestCart, isAuthenticated]);

  // Add to cart mutation
  const addToCartMutation = useMutation({
    mutationFn: async ({ productId, quantity, variantId, size }: { productId: string; quantity: number; variantId: string; size?: string }) => {
      if (isAuthenticated) {
        const payload: any = { productId, quantity, variantId };
        if (size) {
          payload.size = size;
        }
        const response = await api.post('/cart/add', payload);
        return response.data;
      }
      return null;
    },
    onSuccess: () => {
      if (isAuthenticated) {
        queryClient.invalidateQueries({ queryKey: ['cart'] });
      }
    },
  });

  // Update cart item quantity mutation
  const updateQuantityMutation = useMutation({
    mutationFn: async ({ cartItemId, quantity }: { cartItemId: string; quantity: number }) => {
      const response = await api.put(`/cart/items/${cartItemId}`, { quantity });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cart'] });
    },
  });

  // Remove from cart mutation
  const removeFromCartMutation = useMutation({
    mutationFn: async (cartItemId: string) => {
      const response = await api.delete(`/cart/items/${cartItemId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cart'] });
    },
  });

  // Clear cart mutation
  const clearCartMutation = useMutation({
    mutationFn: async () => {
      const response = await api.delete('/cart');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cart'] });
    },
  });

  // Sync cart mutation (for login)
  const syncCartMutation = useMutation({
    mutationFn: async (items: { productId: string; quantity: number; variantId: string }[]) => {
      const response = await api.post('/cart/sync', { items });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cart'] });
      // Clear guest cart after sync
      localStorage.removeItem(GUEST_CART_KEY);
      setGuestCart({ items: [], totalItems: 0, totalAmount: 0 });
    },
  });

  // Add to cart function
  const addToCart = async (productId: string, quantity: number, productDetails?: any, variantId?: string | null, size?: string | null) => {
    try {
      if (isAuthenticated) {
        // For authenticated users, variantId is required
        if (!variantId) {
          throw new Error('Variant ID is required');
        }
        await addToCartMutation.mutateAsync({ productId, quantity, variantId, size: size ?? undefined });
      } else {
        // Guest cart - update localStorage
        setGuestCart((prev) => {
          // Check if same product with same variant and size exists
          const existingItem = prev.items.find((item) => 
            item.productId === productId && 
            item.variantId === variantId &&
            item.size === size
          );

          let newItems: CartItem[];
          if (existingItem) {
            newItems = prev.items.map((item) =>
              item.productId === productId && 
              item.variantId === variantId &&
              item.size === size
                ? { ...item, quantity: item.quantity + quantity, subtotal: item.productPrice * (item.quantity + quantity) }
                : item
            );
          } else {
            // Add new item with product details
            if (!productDetails) {
              throw new Error('Product details required for guest cart');
            }
            const newItem: CartItem = {
              id: `guest-${Date.now()}`,
              productId,
              variantId: variantId || undefined,
              productName: productDetails.name,
              productPrice: productDetails.price,
              productImage: productDetails.imageUrl || '',
              size: size || null,
              quantity,
              subtotal: productDetails.price * quantity,
            };
            newItems = [...prev.items, newItem];
          }

          const totalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
          const totalAmount = newItems.reduce((sum, item) => sum + item.subtotal, 0);

          return { items: newItems, totalItems, totalAmount };
        });
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to add to cart');
      throw error;
    }
  };

  // Update quantity function
  const updateQuantity = async (cartItemId: string, quantity: number) => {
    try {
      if (isAuthenticated) {
        await updateQuantityMutation.mutateAsync({ cartItemId, quantity });
      } else {
        // Update guest cart
        setGuestCart((prev) => {
          const newItems = prev.items.map((item) =>
            item.id === cartItemId
              ? { ...item, quantity, subtotal: item.productPrice * quantity }
              : item
          );

          const totalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
          const totalAmount = newItems.reduce((sum, item) => sum + item.subtotal, 0);

          return { items: newItems, totalItems, totalAmount };
        });
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update quantity');
      throw error;
    }
  };

  // Remove from cart function
  const removeFromCart = async (cartItemId: string) => {
    try {
      if (isAuthenticated) {
        await removeFromCartMutation.mutateAsync(cartItemId);
        message.success('Removed from cart');
      } else {
        // Remove from guest cart
        setGuestCart((prev) => {
          const newItems = prev.items.filter((item) => item.id !== cartItemId);

          const totalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
          const totalAmount = newItems.reduce((sum, item) => sum + item.subtotal, 0);

          return { items: newItems, totalItems, totalAmount };
        });
        message.success('Removed from cart');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to remove from cart');
      throw error;
    }
  };

  // Clear cart function
  const clearCart = async () => {
    try {
      if (isAuthenticated) {
        await clearCartMutation.mutateAsync();
      } else {
        setGuestCart({ items: [], totalItems: 0, totalAmount: 0 });
      }
      message.success('Cart cleared');
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to clear cart');
      throw error;
    }
  };

  // Sync cart on login
  const syncCartOnLogin = async () => {
    const stored = localStorage.getItem(GUEST_CART_KEY);
    if (stored) {
      try {
        const guestCartData = JSON.parse(stored);
        if (guestCartData.items && guestCartData.items.length > 0) {
          const items = guestCartData.items
            .filter((item: CartItem) => item.variantId) // Only sync items with variantId
            .map((item: CartItem) => ({
              productId: item.productId,
              quantity: item.quantity,
              variantId: item.variantId!,
              size: item.size,
            }));
          if (items.length > 0) {
            await syncCartMutation.mutateAsync(items);
            message.success('Cart synced successfully');
          }
        }
      } catch (error) {
        console.error('Failed to sync cart:', error);
      }
    }
  };

  // Return appropriate cart based on authentication
  const cart = isAuthenticated ? serverCart : guestCart;

  return {
    cart: cart || { items: [], totalItems: 0, totalAmount: 0 },
    isLoading: isAuthenticated ? isLoading : false,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    syncCartOnLogin,
  };
};

